import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import NavBar from '../components/NavBar/Navigation';
import Footer from '../components/Footer/Footer';
import BenefitsBannerImage from '../assets/benefits.png';
import './EmployeeBenefits.css';

// --- CONFIG ------------------------------------------------------------------
const BOARD_ID  = process.env.REACT_APP_MONDAY_BENEFITS_BOARD_ID;
const API_TOKEN = process.env.REACT_APP_MONDAY_TOKEN;

const SECTION_COLUMN_ID  = 'dropdown_mm2dnqsb';
const DOCUMENT_COLUMN_ID = 'file_mm2drj9r';

// SharePoint - Central Resource Library > Employee Benefits > Discounts & Offers
const DISCOUNTS_FOLDER_URL =
  'https://govmt.sharepoint.com/sites/PG-CentralResourceLibrary/Shared%20Documents/Forms/AllItems.aspx?id=%2Fsites%2FPG%2DCentralResourceLibrary%2FShared%20Documents%2FCentral%20Resource%20Library%2FEmployee%20Benefits%2FDiscounts%20%26%20Offers&viewid=91145e56%2D711f%2D4831%2Db8db%2D9108a1d9014b&csf=1&CID=f6e336a2%2D4039%2D0001%2D2a23%2D16cbf595edd4&FolderCTID=0x0120008064C3B474296C41B11D4A88FB126A46';

// The brochure itself (PDF)
const DISCOUNTS_BROCHURE_URL =
  'https://govmt.sharepoint.com/:b:/r/sites/PG-CentralResourceLibrary/Shared%20Documents/Central%20Resource%20Library/Employee%20Benefits/Discounts%20%26%20Offers/Project%20Green%20-%20Discounts%20%26%20Offers.pdf?d=wc9685c9b145a4dd8b3a41ac4f17c161c&csf=1&web=1&e=ZDCXOn';

// --- GRAPHQL QUERY -----------------------------------------------------------
const BENEFITS_QUERY = `
  query GetBenefits {
    boards(ids: [${BOARD_ID}]) {
      items_page(limit: 50) {
        items {
          id
          name
          column_values(ids: ["${SECTION_COLUMN_ID}", "${DOCUMENT_COLUMN_ID}"]) {
            id
            text
            ... on DropdownValue {
              values {
                label
              }
            }
            ... on FileValue {
              files {
                ... on FileAssetValue {
                  asset {
                    id
                    name
                    public_url
                    file_extension
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

// --- HELPERS -----------------------------------------------------------------
function getFileType(extension) {
  if (!extension) return 'other';
  const ext = extension.toLowerCase().replace('.', '');
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return 'image';
  if (ext === 'pdf') return 'pdf';
  return 'other';
}

function mapItems(items) {
  return items.map((item) => {
    const sectionCol  = item.column_values.find((c) => c.id === SECTION_COLUMN_ID);
    const documentCol = item.column_values.find((c) => c.id === DOCUMENT_COLUMN_ID);
    const category    = sectionCol?.values?.[0]?.label || sectionCol?.text || 'General';
    const asset       = documentCol?.files?.[0]?.asset ?? null;
    const extension   = asset?.file_extension ?? '';

    return {
      id:        item.id,
      title:     item.name,
      category,
      type:      getFileType(extension),
      assetUrl:  asset?.public_url || null,
      fileName:  asset?.name || item.name,
      extension: extension.toLowerCase().replace('.', ''),
    };
  });
}

// --- HOOK --------------------------------------------------------------------
function useBenefits() {
  const [benefits, setBenefits] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch('https://api.monday.com/v2', {
          method:  'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization:  `Bearer ${API_TOKEN}`,
            'API-Version':  '2024-01',
          },
          body: JSON.stringify({ query: BENEFITS_QUERY }),
        });

        if (!res.ok) throw new Error(`Monday API error: ${res.status}`);
        const json = await res.json();
        if (json.errors?.length) throw new Error(json.errors[0].message);

        const items = json?.data?.boards?.[0]?.items_page?.items ?? [];
        setBenefits(mapItems(items));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return { benefits, loading, error };
}

// --- SKELETON ----------------------------------------------------------------
function SkeletonCard() {
  return (
    <div className="eb-card eb-card--skeleton" aria-hidden="true">
      <div className="eb-card-left">
        <div className="eb-skeleton-block eb-skeleton-icon" />
      </div>
      <div className="eb-card-divider" />
      <div className="eb-card-right">
        <div className="eb-skeleton-block eb-skeleton-line eb-skeleton-line--short" />
        <div className="eb-skeleton-block eb-skeleton-line" />
        <div className="eb-skeleton-block eb-skeleton-line eb-skeleton-line--medium" />
      </div>
    </div>
  );
}

// --- BROCHURE ARTWORK (pure SVG, no image asset needed) ----------------------
function BrochureArt() {
  return (
    <svg viewBox="0 0 300 330" className="eb-promo-doc" aria-hidden="true">
      <defs>
        <linearGradient id="ebDocHeader" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#006a53" />
          <stop offset="100%" stopColor="#00453a" />
        </linearGradient>
        <clipPath id="ebDocClip">
          <rect x="66" y="24" width="180" height="262" rx="14" />
        </clipPath>
      </defs>

      {/* sheet behind */}
      <rect
        x="44" y="46" width="180" height="248" rx="14"
        fill="#ffffff" opacity="0.2"
        transform="rotate(-9 134 170)"
      />

      {/* front sheet */}
      <g transform="rotate(4 156 155)">
        <rect x="66" y="24" width="180" height="262" rx="14" fill="#ffffff" />

        <g clipPath="url(#ebDocClip)">
          <rect x="66" y="24" width="180" height="118" fill="url(#ebDocHeader)" />
          <circle cx="242" cy="38"  r="42" fill="#dedc00" opacity="0.85" />
          <circle cx="76"  cy="138" r="28" fill="#f192b0" opacity="0.5" />

          <rect x="88" y="70" width="98" height="10" rx="5" fill="#ffffff" />
          <rect x="88" y="90" width="66" height="10" rx="5" fill="#ffffff" opacity="0.66" />

          <rect x="88" y="172" width="140" height="7" rx="3.5" fill="#e6ebe8" />
          <rect x="88" y="188" width="112" height="7" rx="3.5" fill="#e6ebe8" />
          <rect x="88" y="204" width="132" height="7" rx="3.5" fill="#e6ebe8" />
          <rect x="88" y="220" width="90"  height="7" rx="3.5" fill="#e6ebe8" />

          <rect x="88"  y="246" width="60" height="18" rx="9" fill="#f39200" />
          <rect x="156" y="246" width="40" height="18" rx="9" fill="#f192b0" opacity="0.65" />
        </g>

        <rect x="66" y="24" width="180" height="262" rx="14" fill="none" stroke="rgba(14,33,29,0.08)" />
      </g>

      {/* PDF chip */}
      <g transform="rotate(-7 82 276)">
        <rect x="40" y="258" width="84" height="34" rx="17" fill="#ffffff" />
        <text
          x="82" y="280" textAnchor="middle"
          fontSize="13" fontWeight="800" letterSpacing="1.4"
          fill="#005145"
        >
          PDF
        </text>
      </g>
    </svg>
  );
}

// --- DISCOUNTS PROMO ---------------------------------------------------------
function DiscountsPromo() {
  return (
    <section className="eb-promo" aria-labelledby="eb-promo-title">
      <div className="eb-promo-body">
        <span className="eb-promo-tag">
          <span className="eb-promo-tag-dot" aria-hidden="true" />
          Employee perks
        </span>

        <h2 className="eb-promo-title" id="eb-promo-title">Discounts &amp; Offers</h2>

        <p className="eb-promo-copy">
          Savings on retail, wellbeing, travel and everyday spending, arranged for Project Green
          employees. Read the brochure for the full list, or open the Central Resource Library to see every offer as
          it is added.
        </p>

        <div className="eb-promo-actions">
          <a
            className="eb-promo-btn eb-promo-btn--primary"
            href={DISCOUNTS_BROCHURE_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
            </svg>
            Read the brochure
          </a>

          <a
            className="eb-promo-btn eb-promo-btn--ghost"
            href={DISCOUNTS_FOLDER_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Browse the library
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
              <line x1="7" y1="17" x2="17" y2="7" />
              <polyline points="8 7 17 7 17 16" />
            </svg>
          </a>
        </div>

        <p className="eb-promo-note">Both links open in a new tab.</p>
      </div>

      <div className="eb-promo-visual">
        <BrochureArt />
      </div>
    </section>
  );
}

// --- FILE MODAL --------------------------------------------------------------
// No fetch(), no blob, no proxy.
// Images render via an img tag, PDFs via an iframe.
// Both render directly from the signed S3 public_url.
function FileModal({ benefit, onClose }) {
  const [imgError, setImgError] = useState(false);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const isPdf   = benefit.type === 'pdf';
  const isImage = benefit.type === 'image';

  return (
    <AnimatePresence>
      <motion.div
        className="eb-lightbox-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={benefit.title}
      >
        <motion.div
          className={`eb-lightbox-content${isPdf ? ' eb-lightbox-content--pdf' : ''}`}
          initial={{ opacity: 0, scale: 0.94, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 24 }}
          transition={{ duration: 0.26, ease: [0.16, 1, 0.3, 1] }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="eb-lightbox-header">
            <div className="eb-lightbox-header-left">
              <span className={`eb-lightbox-type-badge eb-lightbox-type-badge--${benefit.type}`}>
                {benefit.extension?.toUpperCase() || benefit.type.toUpperCase()}
              </span>
              <span className="eb-lightbox-title">{benefit.title}</span>
            </div>
            <button className="eb-lightbox-close" onClick={onClose} aria-label="Close">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Body - src set directly, no fetch */}
          <div className="eb-lightbox-body">
            {isImage && !imgError && (
              <img
                src={benefit.assetUrl}
                alt={benefit.title}
                className="eb-lightbox-img"
                onError={() => setImgError(true)}
              />
            )}
            {isImage && imgError && (
              <div className="eb-modal-error">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" width="40" height="40">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p>Could not load image.</p>
              </div>
            )}
            {isPdf && (
              <iframe
                src={benefit.assetUrl}
                title={benefit.title}
                className="eb-lightbox-iframe"
              />
            )}
            {!isImage && !isPdf && (
              <div className="eb-modal-generic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" width="48" height="48">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <p>{benefit.fileName}</p>
                <a href={benefit.assetUrl} target="_blank" rel="noopener noreferrer" className="eb-modal-download-btn">
                  <span>Open file</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                    <line x1="7" y1="17" x2="17" y2="7" />
                    <polyline points="8 7 17 7 17 16" />
                  </svg>
                </a>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// --- CARD ICON ---------------------------------------------------------------
function CardIcon({ type, assetUrl }) {
  if (type === 'pdf') {
    return (
      <div className="eb-card-icon eb-card-icon--pdf">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
        <span>PDF</span>
      </div>
    );
  }
  if (type === 'image') {
    return (
      <div className="eb-card-icon eb-card-icon--image">
        {assetUrl ? (
          <>
            {/* scaled + blurred copy of the SAME image, fills the leftover
                space so the cover reads edge-to-edge with zero cropping */}
            <div
              className="eb-card-thumb-fill"
              style={{ backgroundImage: `url(${assetUrl})` }}
              aria-hidden="true"
            />
            <img src={assetUrl} alt="preview" className="eb-card-thumbnail" />
          </>
        ) : (
          <>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
            <span>IMG</span>
          </>
        )}
      </div>
    );
  }
  return (
    <div className="eb-card-icon eb-card-icon--other">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
      <span>FILE</span>
    </div>
  );
}

function actionLabel(type) {
  if (type === 'pdf')   return 'View PDF';
  if (type === 'image') return 'View';
  return 'Open File';
}

// --- PAGE --------------------------------------------------------------------
const EmployeeBenefits = () => {
  const { benefits, loading, error } = useBenefits();
  const [modal, setModal] = useState(null);

  return (
    <div className="eb-page">
      <NavBar />

      <div className="eb-banner" style={{ backgroundImage: `url(${BenefitsBannerImage})` }}>
        <div className="eb-banner-overlay" />
        <div className="eb-banner-text">
          <h2 className="eb-banner-heading">Employee Wellbeing</h2>
          <p className="eb-banner-sub">Designed To Support You</p>
        </div>
      </div>

      <div className="eb-page-content">
        <DiscountsPromo />

        <div className="eb-feed">

          {error && (
            <div className="eb-fetch-error" role="alert">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" width="20" height="20">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <span>Failed to load benefits: {error}</span>
            </div>
          )}

          {loading
            ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
            : benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.id}
                  className="eb-card"
                  initial={{ opacity: 0, filter: 'blur(6px)', y: 20 }}
                  animate={{ opacity: 1, filter: 'blur(0px)', y: 0 }}
                  exit={{ opacity: 0, filter: 'blur(6px)', y: 20 }}
                  transition={{ duration: 0.5, ease: 'easeOut', delay: index * 0.07 }}
                  onClick={() => benefit.assetUrl && setModal(benefit)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && benefit.assetUrl && setModal(benefit)}
                  aria-label={`View ${benefit.title}`}
                  style={!benefit.assetUrl ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                >
                  <div className="eb-card-left">
                    <CardIcon type={benefit.type} assetUrl={benefit.assetUrl} />
                  </div>
                  <div className="eb-card-divider" />
                  <div className="eb-card-right">
                    <div className="eb-card-meta">
                      <span className="eb-card-category">{benefit.category}</span>
                    </div>
                    <h2 className="eb-card-title">{benefit.title}</h2>
                    {benefit.assetUrl ? (
                      <span className="eb-card-action">
                        {actionLabel(benefit.type)}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                          <line x1="5" y1="12" x2="19" y2="12" />
                          <polyline points="12 5 19 12 12 19" />
                        </svg>
                      </span>
                    ) : (
                      <span className="eb-card-no-file">No file attached</span>
                    )}
                  </div>
                </motion.div>
              ))
          }

          {!loading && !error && benefits.length === 0 && (
            <div className="eb-empty">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" width="40" height="40">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
              </svg>
              <p>No benefits found.</p>
            </div>
          )}

        </div>
      </div>

      {modal && <FileModal benefit={modal} onClose={() => setModal(null)} />}

      <Footer />
    </div>
  );
};

export default EmployeeBenefits;